/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 22/7/21.
 */

import * as _ from "lodash";
import chainTypes from "@/enums/ChainTypes";
import ConferenceRoles from "@/enums/ConferenceRoles";
import SequencerModes from "@/enums/SequencerModes";
import Websocket from "@/services/Websocket";
import WorkerTimer from "@/plugins/WorkerTimer";

export default {
	state: {
		websocket: null,
		sequencerSnapshotIntervalsMap: {},
		sequencerModePending: {},
		sequencerActionPending: {},
		sequencerStartStopPlaylistStatus: {pending: false, statusCode: null, message: null}
	},

	getters: {
		sequencerModePending: (state) => (stationID) => {
			return state.sequencerModePending[stationID] || false;
		},

		sequencerActionPending: (state) => (stationID) => {
			return state.sequencerActionPending[stationID]?.pending ?? false;
		},

		sequencerStartStopPlaylistStatus: (state) => (stationID) => {
			return state.sequencerStartStopPlaylistStatus[stationID];
		},

		getSequencerSnapshotIntervalId: (state) => (stationID) => {
			return state.sequencerSnapshotIntervalsMap[stationID];
		}
	},

	mutations: {
		setSequencerModePendingStatus(state, {stationID, status}) {
			if (status === true) {
				state.sequencerModePending[stationID] = status;
			}
			else {
				delete state.sequencerModePending[stationID];
			}
		},

		setSequencerActionStatus(state, {stationID, status}) {
			if (status === true) {
				state.sequencerActionPending[stationID] = status;
			}
			else {
				delete state.sequencerActionPending[stationID];
			}
		},

		setSequencerStartStopPlaylistStatus(state, {stationID, data}) {
			state.sequencerStartStopPlaylistStatus[stationID] = data;
		},

		setSequencerSnapshotIntervalId(state, {stationID, intervalId = null}) {
			if (intervalId) {
				if (!state.sequencerSnapshotIntervalsMap[stationID]) {
					state.sequencerSnapshotIntervalsMap[stationID] = {intervalId, counter: 0};
				}
				state.sequencerSnapshotIntervalsMap[stationID].counter++;
			}
			else {
				state.sequencerSnapshotIntervalsMap[stationID].counter--;
				if (state.sequencerSnapshotIntervalsMap[stationID].counter === 0) {
					delete state.sequencerSnapshotIntervalsMap[stationID];
				}
			}
		}
	},

	actions: {
		openWebsocket() {
			Websocket.initialize();
		},

		closeWebsocket() {
			Websocket.closeConnection();
		},

		refreshWebsocketConnection() {
			Websocket.refreshConnection();
		},

		stationSubscription(state, {stationID, subscribe = true} = {}) {
			Websocket.stationSubscription(stationID, subscribe);
		},

		coreMessageSubscription({getters}) {
			Websocket.coreMessageSubscription(getters.getOrgID());
		},

		sequencerSnapshotSubscription({getters}, {stationID, subscribe = true} = {}) {
			Websocket.sequencerSnapshotSubscription(
				stationID,
				getters.getOrgID(),
				subscribe
			);
		},

		sequencerSnapshotRequest({getters}, stationID) {
			Websocket.sequencerSnapshotRequest(getters.getOrgID(), stationID, {
				Source: getters.getUserFullName(),
				// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
			});
		},

		startSequencerSnapshotExtendRequestInterval({commit, dispatch}, stationID) {
			const intervalId = WorkerTimer.setInterval(() => {
				dispatch("sequencerSnapshotExtendRequest", stationID);
			}, 2 * 60 * 1000); //every 2 minutes

			commit("setSequencerSnapshotIntervalId", {stationID, intervalId});
		},

		clearSequencerSnapshotExtendRequestInterval({getters, commit}, stationID) {
			const {intervalId, counter} = getters.getSequencerSnapshotIntervalId(stationID);
			if (counter === 1) {
				WorkerTimer.clearInterval(intervalId);
			}
			commit("setSequencerSnapshotIntervalId", {stationID});
		},

		sequencerSnapshotExtendRequest({getters}, stationID) {
			Websocket.sequencerSnapshotExtendRequest(stationID, getters.getOrgID(), {
				Source: getters.getUserFullName()
			});
		},

		SequencerStartCustomPlaylist({getters, commit}, {stationID, PlaylistID}) {
			Websocket.SequencerStartCustomPlaylist(
				getters.getOrgID(),
				stationID,
				{
					PlaylistID,
					Source: getters.getUserFullName(),
					// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
				}
			);
			commit(
				"setSequencerStartStopPlaylistStatus",
				{stationID, data: {status: true, statusCode: null, message: null}}
			);
		},

		SequencerStopCustomPlaylist({getters, commit}, {stationID, PlaylistID}) {
			Websocket.SequencerStopCustomPlaylist(
				getters.getOrgID(),
				stationID,
				{
					PlaylistID,
					Source: getters.getUserFullName(),
					// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
				}
			);
			commit("setSequencerStartStopPlaylistStatus", {stationID, data: {pending: true}});
		},

		sequencerStartStopPlaylistResponse({commit}, {stationID, statusCode, message}) {
			commit(
				"setSequencerStartStopPlaylistStatus",
				{
					stationID,
					data: {
						pending: false,
						statusCode,
						message: message.message.charAt(0).toUpperCase() + message.message.slice(1)	//Capitalize the first letter
					}
				}
			);
		},

		playlistSubscription({getters}, {playlistID, subscribe = true}) {
			Websocket.playlistSubscription(playlistID, getters.getOrgID(), subscribe);
		},

		playlistEventSubscription({getters}, {playlistID, subscribe = true}) {
			Websocket.playlistEventSubscription(playlistID, getters.getOrgID(), subscribe);
		},

		assetSubscription({getters}, {assetID, subscribe = true}) {
			Websocket.assetSubscription(assetID, getters.getOrgID(), subscribe);
		},

		stationAssetSubscription({getters}, {assetID, stationID, subscribe = true} = {}) {
			Websocket.stationAssetSubscription(
				stationID,
				assetID,
				getters.getOrgID(),
				subscribe
			);
		},

		sequencerModeRequest({getters, commit}, {stationID, mode}) {
			Websocket.SequencerModeRequest(
				getters.getOrgID(),
				stationID,
				mode,
				{
					Source: getters.getUserFullName(),
					// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
				}
			);
			commit("setSequencerModePendingStatus", {stationID, status: true});
		},

		sequencerModeResponse({commit}, {topic, message, statusCode}) {
			const stationID = topic.split("/")[3];
			if (statusCode !== 200) {
				commit("SHOW_NOTIFICATION_WITH_TYPE", {message: message.message});
			}
			commit("setSequencerModePendingStatus", {stationID, status: false});
		},

		sequencerActionRequest({getters, commit}, {stationID, msg = {}, action}) {
			msg = {
				...msg,
				...{
					Source: getters.getUserFullName(),
					// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
				}
			}

			Websocket.SequencerActionRequest(
				getters.getOrgID(),
				stationID,
				action,
				msg
			);
			commit("setSequencerActionStatus", {stationID, status: true});

			// Fallback: Set the "pending" flag back to false after 1 sec, in case of sequencer's response missed
			WorkerTimer.setTimeout(() => {
				commit("setSequencerActionStatus", {stationID, status: false});
			}, 1000);
		},

		sequencerActionResponse({commit}, {topic, message, statusCode}) {
			const stationID = topic.split("/")[3];
			if (statusCode !== 200) {
				commit("SHOW_NOTIFICATION_WITH_TYPE", {message: message.message});
			}
			commit("setSequencerActionStatus", {stationID, status: false});
		},

		sequencerEndActionRequest({getters, commit}, {stationID, EventID, Chain}) {
			if (chainTypes.Segue === Chain) {
				Websocket.SequencerToggleEndActionRequest(
					getters.getOrgID(),
					stationID,
					EventID,
					"StopChain",
					{
						Source: getters.getUserFullName(),
						// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
					}
				);
			}
			else {
				Websocket.SequencerToggleEndActionRequest(
					getters.getOrgID(),
					stationID,
					EventID,
					"SegueChain",
					{
						Source: getters.getUserFullName(),
						// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
					}
				);
			}
			commit("setSequencerActionStatus", {stationID, status: true});

			// Fallback: Set the "pending" flag back to false after 1 sec, in case of sequencer's response missed
			WorkerTimer.setTimeout(() => {
				commit("setSequencerActionStatus", {stationID, status: false});
			}, 1000);
		},

		sequencerStartLiveRequest({getters}, {assetId, transitionType = 1}) {
			const payload = {
				transitionType, // 0.Immediately (audio jump is imminent), 1.Next event (by Default)
				assetId,
				StudioChannelQuantity: getters.getStudioSettings().pots_count,
				Source: getters.getUserFullName(),
				// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
			};

			Websocket.SequencerStartLiveRequest(getters.getOrgID(), getters.liveModeSelectedStationID(), payload);
		},

		sequencerEndLiveRequest({getters}) {
			Websocket.SequencerEndLiveRequest(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				{
					Source: getters.getUserFullName(),
					// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
				}
			);
		},

		playerSIPConnectRequest({getters}, role) {
			const payload = {
				"studio": getters.getStudioSettings().reference,
				"endpoints": getters.getStudioSettings().sip_connection_address,
				"role": role
			};

			Websocket.PlayerSIPConnectRequest(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				payload
			);

			return Promise.resolve();
		},

		playerSIPDisconnectRequest({getters}) {
			const payload = {
				"studio": getters.getStudioSettings().reference
			};

			Websocket.PlayerSIPDisconnectRequest(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				payload
			);
		},

		playerOnlineSubscription({getters}, subscribe = true) {
			Websocket.PlayerOnlineSubscription(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				getters.getLivePlayerSnapshot?.RealtimePlayerKey,
				subscribe
			);
		},

		playerHeartbeatSubscription({getters}, subscribe = true) {
			Websocket.PlayerHeartbeatSubscription(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				subscribe
			);
		},

		livePlayerSnapshotSubscription({getters}, subscribe = true) {
			Websocket.LivePlayerSnapshotSubscription(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				subscribe
			);
		},

		livePlayerConferenceSubscription({getters}, subscribe = true) {
			Websocket.LivePlayerConferenceSubscription(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				subscribe
			);
		},

		livePlayerLiveMetadataSubscription({getters}, subscribe = true) {
			Websocket.LivePlayerLiveMetadataSubscription(
				getters.liveModeSelectedStationID(),
				subscribe
			);
		},

		livePlayerTransferHostSubscription({getters}, subscribe = true) {
			Websocket.LivePlayerTransferHostSubscription(
				getters.liveModeSelectedStationID(),
				subscribe
			);
		},

		livePlayerKickHostStudioSubscription(state, subscribe = true) {
			Websocket.LivePlayerKickHostStudioSubscription(subscribe);
		},

		livePlayerSnapshotRequest({getters}) {
			Websocket.LivePlayerSnapshotRequest(getters.liveModeSelectedStationID(), getters.getOrgID());
		},

		livePlayerSnapshotResponse({commit, getters}, message) {
			const livePlayerSnapshot = getters.getLivePlayerSnapshot(message.RealtimePlayerKey) || message;
			const studioSettings = getters.getStudioSettings();

			if (!!studioSettings &&		// if studio settings obtained
				!!message.liveMode &&	// and if sequencer is connected to the player
				!!message.sipConnectionFailed &&	// and if there is a sip connection failure message
				!_.isEqual(			// and if the player's snapshot we received is different from the one we already have in state
					_.pick(livePlayerSnapshot, ["liveMode", "sipConnectConfig", "sipConnected", "sipConnectionFailed"]),
					_.pick(message, ["liveMode", "sipConnectConfig", "sipConnected", "sipConnectionFailed"])
				)) {

				// If there is a sip connection error between the player and the studio
				if (message.sipConnected === false &&
					["INVALID_ADDRESS", "INTERNAL_ERROR"].includes(message.sipConnectionFailed) &&
					studioSettings?.sip_connection_address?.includes(message.sipConnectConfig.sipAddress)) {
					commit(
						"SHOW_NOTIFICATION_WITH_TYPE",
						{message: "An error occurred when trying to connect the Studio to the selected Station."}
					);
				}

				// If there is an established sip connection between a player and another studio
				if (message.sipConnected === true &&
					message.sipConnectionFailed === "ON_CALL" &&
					!studioSettings?.sip_connection_address?.includes(message.sipConnectConfig.sipAddress)) {
					commit(
						"SHOW_NOTIFICATION_WITH_TYPE",
						{message: "Another Studio is already connected to the selected Station"}
					);
				}
			}

			if (message.dumpBufferDuration < livePlayerSnapshot?.dumpBufferDuration) {
				commit("SHOW_SUCCESS_NOTIFICATION", {message: "Buffer dumped!"});
			}

			commit("setLivePlayer", message);
			commit("setDumpBufferPendingStatus", false);
		},

		livePlayerConferenceRequest({getters}, playerKey) {
			Websocket.LivePlayerConferenceRequest(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				playerKey
			);
		},

		livePlayerConferenceSnapshotResponse({getters, commit}, {topic, message}) {
			const playerKey = topic.split("/")[4];
			if (getters.getLivePlayerSnapshot(playerKey)) {	//Check if playerKey exists
				commit("setLivePlayerConferenceSnapshot", {playerKey, conferenceSnapshot: message});
			}
		},

		livePlayerToggleStudioMicRequest({getters}, {playerKey, studio, muted}) {
			if (muted) {
				Websocket.LivePlayerUnmuteStudioMicRequest(
					getters.getOrgID(),
					getters.liveModeSelectedStationID(),
					playerKey,
					studio
				);
			}
			else {
				Websocket.LivePlayerMuteStudioMicRequest(
					getters.getOrgID(),
					getters.liveModeSelectedStationID(),
					playerKey,
					studio
				);
			}
		},

		livePlayerActionRequest({getters}, message) {
			Websocket.LivePlayerActionRequest(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				message.action,
				message.body
			);
		},

		livePlayerSetMixingBufferConfiguration({getters}, {playerKey, automatic, adjustment}) {
			Websocket.LivePlayerSetMixingBufferConfiguration(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				playerKey,
				automatic,
				adjustment
			);
		},

		// todo: backward compatibility function. Remove on next astrum release
		livePlayerSetMixingBufferDepth({getters}, {playerKey, depth}) {
			Websocket.LivePlayerSetMixingBufferDepth(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				playerKey,
				depth
			);
		},

		livePlayerTransferHost({getters}, peer) {
			Websocket.LivePlayerTransferHost(
				getters.getOrgID(),
				getters.liveModeSelectedStationID(),
				peer.playerKey,
				peer.studio,
				ConferenceRoles.Host
			);
		},

		livePlayerTransferHostWebRTC({getters}, peer) {
			Websocket.LivePlayerTransferHostWebRTC(
				getters.liveModeSelectedStationID(),
				peer.playerKey,
				peer.studio,
				ConferenceRoles.Host
			);
		},

		livePlayerKickHostStudioWebRTC({getters}, peer) {
			Websocket.LivePlayerKickHostStudioWebRTC(getters.getOrgID(), getters.liveModeSelectedStationID(), peer);
		},

		transferHostWebRTC({dispatch}) {
			dispatch("reopenWebRTCConnection", ConferenceRoles.Host);
		},

		sequencerSyncActionRequest({getters, commit}, {action, msg, stationID}) {
			msg = {
				...msg,
				...{
					Source: getters.getUserFullName(),
					// RealtimeTimestamp: moment.tz(getters.stationTimezone()).toISOString()
				}
			}
			Websocket.SequencerActionRequest(getters.getOrgID(), stationID, action, msg);
			commit("setSyncToSelectionActive", true);
		},

		sequencerSyncActionResponse({getters, commit, dispatch}, {stationID, message, statusCode}) {
			if (statusCode === 200) {
				commit("SHOW_SUCCESS_NOTIFICATION", {message: "Sync to event successfully!"});
				if (getters.getSwitchToAutoMode(stationID) &&
					getters.getSequencerMode(stationID) !== SequencerModes.Auto) {
					dispatch("sequencerModeRequest", {stationID, mode: SequencerModes.Auto});
				}
			}
			else {
				commit("SHOW_NOTIFICATION_WITH_TYPE", {message: message.message});
			}
			commit("setSyncToSelectionActive", false);
		},

		notificationMessageReceived({getters, commit, dispatch}, message) {
			if (message.Product === "Sound+ Live Console") {
				if (!message.ForceLogout) {
					if (message.Action === "Display") {
						if (message.KeepUntilSignalled) {
							commit("SHOW_PERSISTENT_NOTIFICATION", {
								id: message.SystemUserMessageKey,
								title: message.MessageTitle,
								message: message.Message,
								type: message.MessageType
							});
						}
						else {
							commit("SHOW_NOTIFICATION_WITH_TYPE", {
								id: message.SystemUserMessageKey,
								message: message.Message,
								type: message.MessageType
							});
						}
					}
					else if (message.Action === "Update") {
						commit("UPDATE_PERSISTENT_NOTIFICATION", {
							title: message.MessageTitle,
							message: message.Message,
							type: message.MessageType
						})
					}
					else if (message.Action === "Remove") {
						commit("REMOVE_PERSISTENT_NOTIFICATION");
					}
				}
				else {
					if (message.Action === "Display" || message.Action === "Update") {
						commit("SHOW_NOTIFICATION_WITH_ACTION", {
							message: message.Message,
							timeout: -1,
							action: {
								text: "Logout",
								click: () => {
									dispatch("signOut");
								}
							}
						})
					}
					else {
						commit("HIDE_NOTIFICATION", getters.snackbarNotification());
					}
				}
			}
		},

		studioKickMessageReceived({getters, dispatch}, message) {
			// If the kick message targets this station's studio and the kicked peer is the Host, disconnect the studio
			if (
				getters.getOrgID() === message.orgID &&
				getters.liveModeSelectedStationID() === message.stationID &&
				getters.getStudioSettings()?.reference === message.peer.studio
			) {
				if (message.peer.role === ConferenceRoles.Host) {
					dispatch("disconnectStudio", ConferenceRoles.Host);
				}
			}
		}
	}
};
